import _ from 'lodash'
import * as React from 'react'

import Typography from '@mui/material/Typography'
import { House, Control } from '../../types'
import { hexPinCode, isActive } from '../../utils/businessUtil'

type Props = {
	house: House
	controls?: Control[]
}

// 使用中のピン番号を取得する関数
const getUsedPins = (
	controls: Control[],
): { usedPins: Set<number>; twayPins: Set<number> } => {
	const usedPins = new Set<number>()
	const twayPins = new Set<number>()

	controls.forEach((control) => {
		const isTway = control.category === 'tway'

		if (control.activePin !== null && control.activePin >= 0) {
			const displayPin = control.activePin + 1
			usedPins.add(displayPin)
			if (isTway) twayPins.add(displayPin)
		}

		if (control.negativePin !== null && control.negativePin >= 0) {
			const displayPin = control.negativePin + 1
			usedPins.add(displayPin)
			if (isTway) twayPins.add(displayPin)
		}
	})

	return { usedPins, twayPins }
}

type PinUnit = {
	active: boolean
	label: string
	pinNumber: number
}

type PinGraph = {
	up8Pins: PinUnit[]
	down8Pins: PinUnit[]
}

const Lamp = ({
	pin,
	isAssigned,
	isTwayPin,
}: {
	pin: PinUnit
	isAssigned: boolean
	isTwayPin: boolean
}) => {
	let bgColor: string
	let opacity: number

	if (isTwayPin) {
		bgColor = '#4caf50'
		opacity = 1.0
	} else {
		bgColor = pin.active ? 'orange' : '#aaa'
		opacity = isAssigned ? 1.0 : 0.5
	}

	return (
		<div
			style={{
				flex: 1,
				textAlign: 'center',
				color: 'white',
				borderLeft: isTwayPin ? 'solid #2e7d32 5px' : 'solid #000088 5px',
				borderBottom: isTwayPin ? 'solid #2e7d32 5px' : 'solid #000088 5px',
				background: bgColor,
				opacity,
				transition: 'opacity 0.2s',
			}}
			title={
				isTwayPin
					? `${pin.label}: 三方弁用`
					: isAssigned
					? pin.active
						? `${pin.label}: 割当済み（ON）`
						: `${pin.label}: 割当済み（OFF）`
					: pin.active
					? `${pin.label}: 未割当（ON）`
					: `${pin.label}: 未割当（OFF）`
			}
		>
			{pin.label}
		</div>
	)
}

function toPinGraph(n: number): PinGraph {
	return {
		up8Pins: _.range(8, 0, -1).map((i) => ({
			label: `K${i}`,
			active: isActive(n, i - 1),
			pinNumber: i,
		})),
		down8Pins: _.range(9, 17).map((i) => ({
			label: `K${i}`,
			active: isActive(n, i - 1),
			pinNumber: i,
		})),
	}
}

const PinState = (props: Props) => {
	const pinGraph = toPinGraph(props.house.pin)
	const pinBinary = props.house.pin.toString(2).padStart(16, '0')

	// 割当済みピンと三方弁ピンを取得
	const { usedPins, twayPins } = getUsedPins(props.controls || [])

	return (
		<div>
			<Typography variant="subtitle1">
				pin: {props.house.pin} ({hexPinCode(props.house.pin)}, 0b{pinBinary})
			</Typography>
			<div style={{ maxWidth: '300px', width: '100%' }}>
				<div style={{ display: 'flex' }}>
					{pinGraph.up8Pins.map((pin, i) => (
						<Lamp
							key={i}
							pin={pin}
							isAssigned={usedPins.has(pin.pinNumber)}
							isTwayPin={twayPins.has(pin.pinNumber)}
						/>
					))}
				</div>
				<div style={{ display: 'flex' }}>
					{pinGraph.down8Pins.map((pin, i) => (
						<Lamp
							key={i}
							pin={pin}
							isAssigned={usedPins.has(pin.pinNumber)}
							isTwayPin={twayPins.has(pin.pinNumber)}
						/>
					))}
				</div>
			</div>
		</div>
	)
}

export default PinState
